// D03 Mission 01 — Routing Fundamentals (LEARN)
import { readConfirm, predict, observe } from '../../ui/widgets.js';
import { regional, staticDesign, regionalLayout, S } from './topologies.js';
import { ran, pinged, norm, no, bestRoute } from './helpers.js';

function network() {
  const n = staticDesign(regional({ backup: false }));
  const { R2 } = n.devices;
  S(R2, '10.30.0.0', '255.255.0.0', 'Null0');
  S(R2, '0.0.0.0', '0.0.0.0', '198.51.100.1');
  S(R2, '10.30.2.0', '255.255.255.0', '10.30.12.2', 250); // floating backup (not installed)
  return n;
}

export default {
  id: 'u01', num: '01', kind: 'LEARN', title: 'Routing Fundamentals', type: 'Lesson', implemented: true,
  objective: 'Read a routing table like an engineer: connected and local routes, next hops, longest-prefix match, administrative distance and metric — then prove each idea on R2.',
  network, layout: regionalLayout({ backup: false, proto: { a: 'static', b: 'static', isp: 'static default' } }), linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'What a router actually decides', workspace: false,
      content: `<p>Welcome to the Davao region. Campus switching kept traffic <i>inside</i> a site; now packets have to cross between sites. A <b>router</b> connects different IP networks and, for every packet, answers one question: <b>which interface, and which next hop?</b></p>
<h3>The routing table</h3>
<pre>Gateway of last resort is 198.51.100.1 to network 0.0.0.0

S*    0.0.0.0/0 [1/0] via 198.51.100.1
C        10.30.12.0/30 is directly connected, GigabitEthernet0/1
L        10.30.12.1/32 is directly connected, GigabitEthernet0/1
S        10.30.1.0/24 [1/0] via 10.30.12.2
S        10.30.0.0/16 is directly connected, Null0</pre>
<ul>
<li><b>C — connected</b>: every interface that is up/up with an IP address adds its subnet automatically. <b>L — local</b> is the router's own address (/32).</li>
<li><b>Next hop</b> (<code>via 10.30.12.2</code>): the neighbouring router the packet is handed to. It must be reachable on a connected subnet.</li>
<li><code>[1/0]</code> = <b>[administrative distance / metric]</b>.</li>
</ul>
<h3>Longest-prefix match</h3>
<p>When several entries contain the destination, the router uses the <b>most specific</b> one (largest prefix length) — not the first, not the newest. 10.30.1.77 matches 0.0.0.0/0, 10.30.0.0/16 and 10.30.1.0/24; the /24 wins.</p>
<h3>Administrative distance vs metric</h3>
<table><tr><th>Source</th><th>AD</th></tr><tr><td>Connected</td><td>0</td></tr><tr><td>Static</td><td>1</td></tr><tr><td>eBGP</td><td>20</td></tr><tr><td>EIGRP</td><td>90</td></tr><tr><td>OSPF</td><td>110</td></tr><tr><td>RIP</td><td>120</td></tr><tr><td>iBGP</td><td>200</td></tr></table>
<ul><li><b>AD</b> decides between <i>different sources</i> offering the same prefix — lower is trusted more. A static route with AD 250 stays in the configuration but only enters the table when the better route disappears (a <i>floating</i> static).</li>
<li><b>Metric</b> decides between paths learned by the <i>same</i> protocol (hops for RIP, cost for OSPF, composite metric for EIGRP).</li></ul>`,
      widgets: [readConfirm({ id: 'd3u01-read' })],
      question: 'Why can a router forward a packet for a network it has never seen, and when is that a problem?',
    },
    {
      id: 'predict', phase: 'UNDERSTAND', title: 'Predict before you look', workspace: false,
      content: '<p>R2 (Regional HQ) has a default route to the ISP, a summary 10.30.0.0/16 to Null0, and specific routes to each branch.</p>',
      widgets: [predict({ id: 'd3u01-pred', prompt: 'A packet for 10.30.1.77 arrives at R2. Which entry forwards it?', options: ['0.0.0.0/0 via the ISP', '10.30.0.0/16 to Null0', '10.30.1.0/24 via 10.30.12.2'] })],
    },
    {
      id: 'verify', phase: 'VERIFY', title: 'Prove it on R2', devices: ['R2', 'PC-A', 'PC-B'],
      content: '<p>Use <code>show ip route</code>, and <code>show ip route &lt;address&gt;</code> to ask the router which entry it would use. Then send real traffic.</p>',
      evidence: [
        { id: 'rt', text: 'R2: full routing table displayed', match: ran('R2', 'show ip route') },
        { id: 'lpm', text: 'R2: lookup for 10.30.1.77', match: (ev) => ran('R2', 'show ip route <A.B.C.D>')(ev) && ev.args?.[0] === '10.30.1.77' },
        { id: 'ping', text: 'PC-A reaches PC-B through R2', match: pinged('PC-A', '10.30.2.10', { success: true }) },
      ],
      widgets: [observe({
        id: 'd3u01-obs', title: 'READ THE TABLE',
        fields: [
          { label: 'Prefix R2 uses for 10.30.1.77', placeholder: 'a.b.c.d/nn', check: (v, rt) => norm(v) === `${bestRoute(rt, 'R2', '10.30.1.77')[0].net}/${bestRoute(rt, 'R2', '10.30.1.77')[0].prefix}` || 'Look for the most specific matching entry.' },
          { label: 'Next hop for that prefix', check: (v, rt) => norm(v) === bestRoute(rt, 'R2', '10.30.1.77')[0].nh || 'The "via" address of that entry.' },
          { label: 'Entry that handles 10.30.99.5 (no such subnet exists)', placeholder: 'a.b.c.d/nn', check: (v) => norm(v) === '10.30.0.0/16' || 'Which entry is the most specific match for 10.30.99.5?' },
          { label: 'Administrative distance of the default route', check: (v) => norm(v) === '1' || 'Look at [AD/metric] on the S* line.' },
          { label: 'Is the AD 250 route to 10.30.2.0/24 in the table? (yes/no)', check: (v) => no(v) || 'Compare show ip route with the configuration: floating statics wait outside the table.' },
        ],
      })],
      question: 'What would happen to traffic for 10.30.99.5 if the Null0 summary were removed?',
    },
  ],
  review: () => '<p>Most specific match wins; AD chooses between sources; metric chooses between paths of one protocol. The Null0 summary discards traffic for unused 10.30.x.x space instead of sending it to the ISP — you will see why that matters in Mission 03.</p>',
};
